import { initGlobalState, addErrorHandler, removeErrorHandler } from 'qiankun'
import { useAppStore } from '@/store'
import { isObject } from 'lodash'
import { cached } from '@/utils/utils'
import { trackEvent } from '@/plugins/plugin-init-omega'
import router from '@/router'
import { gtrTrackSW, gtrCollectTrackCommonData } from '@/utils/gattaran-track-event'
import { gtrAuth, gtrAuths } from '@/utils/gtr-auth'
import { getMicroApps, getIgnoreScript, matchMicroAppLocation } from '@/config/apps'
import { gtrSendEvents, eventBus } from './event'
import { setPerformanceTiming } from '@/plugins/performance-tools/performance-tools'
import { setUserTimezone } from '../plugins/elvish'
import { changeErrorFilters, initErrorFilters } from '@/utils/error-filter'
export {
  gtrListenEvents,
  GTR_LISTEN_EVENT_LIST,
  GTR_SEND_EVENT_LIST,
  gtrSendEvents,
  eventBus,
} from './event'

/**
 * MICRO_APP配置已迁移至 src/config/apps.js
 * 本地调试修改 src/config/apps.dev.js
 */

// 微应用项目名称规则
// (b|c|d|i|gattaran) + (_|-) + 业务名称 + (_|-) + i18n
const MICRO_APP_NAME_RULE = /^(?:b|c|d|i|gattaran)(?:_|-)([\w-]+)(?:_|-)i18n$/
const origin = window.location.origin

export const checkMicroAppName = name => {
  return MICRO_APP_NAME_RULE.test(name)
}
const actions = initGlobalState({})
const initBreadcrumb = () => {
  globalStateActions.setGlobalState({ showBreadcrumb: true })
  globalStateActions.setGlobalState({ subBreadcrumb: [] })
}
// 微应用的入口
// dev环境默认为 http://localhost:8080
const getMicroEntry = config => {
  /* @__PURE__ */
  if (process.env.NODE_ENV === 'development') {
    return config.devEntry ? config.devEntry : 'http://localhost:8080'
  }
  return origin + (config.entry ? config.entry : config.appLocation[0])
}
// 微应用挂载点
const getMicroContainer = config => {
  return `#${microAppContainer}`
}
// 子系统添加的路由前缀
// gtr_{子系统项目名称}
const getPrefix = ({ name }) => {
  if (!checkMicroAppName(name)) {
    throw Error(`ERROR: 微应用项目名称不规范 项目名称${name}`)
  }
  const [, projectName] = name.match(MICRO_APP_NAME_RULE)
  return `/gtr_${projectName}`
}
// 获取激活规则
// baseUrl + prefix
const getActiveRule = prefix => {
  const BASE_URL = process.env.VUE_APP_ROUTER_BASE
  return `${BASE_URL && BASE_URL.length > 1 ? BASE_URL : ''}${prefix}`
}
// 微应用Props
const formatAppProps = (props, config) => {
  return {
    ...props,
    GTR_UTILS: {
      ...props.GTR_UTILS,
      appConfig: {
        ...config,
        base: `${config.activeRule}/`,
      },
    },
  }
}

const formatApps = apps => {
  return apps.map(config => {
    const entry = getMicroEntry(config)
    const container = getMicroContainer(config)
    const prefix = getPrefix(config)
    const activeRule = getActiveRule(prefix, config)
    return {
      ...config,
      entry,
      container,
      prefix,
      activeRule,
    }
  })
}
const memoryInfo = () => {
  const { jsHeapSizeLimit = 0, totalJSHeapSize = 0, usedJSHeapSize = 0 } = performance?.memory || {}
  return {
    limit: ~~(jsHeapSizeLimit / 1024 / 1024),
    total: ~~(totalJSHeapSize / 1024 / 1024),
    used: ~~(usedJSHeapSize / 1024 / 1024),
  }
}
let prevMemoryInfo = memoryInfo()
const formatMemoryInfo = () => {
  const currMemoryInfo = memoryInfo()
  const sys_used = prevMemoryInfo.used - currMemoryInfo.used
  const info = {
    ...currMemoryInfo,
    prev_used: prevMemoryInfo.used,
    prev_total: prevMemoryInfo.total,
    sys_used,
    abs_sys_used: Math.abs(sys_used),
  }
  prevMemoryInfo = currMemoryInfo
  return info
}
const microAppSendUseMemoryLog = (name, type) => {
  trackEvent('sailing_m_gtr_use_memory_bt', '内存溢出埋点', {
    name, // 系统名称
    type, // beforeLoad|beforeMount｜afterUnmount
    ...formatMemoryInfo(),
  })
}
const microAppSendErrorLog = e => {
  trackEvent('sailing_m_gtr_load_micro_app_error_bt', '微前端错误日志埋点', {
    error_message: e.message, // 错误日志信息
    micro_app_name: e.appOrParcelName, // 应用名称
    error_name: e.name,
    error_stack: e.stack,
    url: window.location.href,
  })
}
// 子系统dom挂载点ID
export const microAppContainer = 'micro-app-container'
// 微应用全集
let QIAN_KUN_CONFIG
export const getMicroAppsQianKunConfig = () => {
  if (QIAN_KUN_CONFIG) return QIAN_KUN_CONFIG
  QIAN_KUN_CONFIG = {
    apps: formatApps(getMicroApps()),
    ignoreScript: getIgnoreScript(),
  }
  return QIAN_KUN_CONFIG
}
// 获取微应用配置列表
export const getMicroAppsQianKunProps = props => {
  return getMicroAppsQianKunConfig().apps.map(appConfig => {
    return {
      ...appConfig,
      props: {
        GTR_MICRO_APPS: getMicroAppsQianKunConfig().apps,
        ...formatAppProps(props, appConfig),
      },
    }
  })
}
// bugfix 重写push 子应用内push基座路由会重复跳转v2
export const gtrPush = (...arg) => {
  try {
    let path = arg[0]
    if (isObject(path) && path.path) {
      path = path.path
    }
    console.warn(`非子系统路由触发基座push`, path)
    if (/^\/v2\//.test(path)) {
      console.warn(`path: ${path} 为基座路由，不跳转`)
      return
    }
    router.push(...arg)
  } catch (e) {
    console.error(e)
  }
}
export const gtrRouter = {
  push: gtrPush,
  replace(...arg) {
    return router.replace(...arg)
  },
}

// 微应用插件
export const gtrVuePlugin = {
  install(Vue, options = {}) {
    Vue.prototype.$gtrEventBus = eventBus
    Vue.prototype.$gtrRouter = gtrRouter
    Vue.prototype.$gtrAuth = gtrAuth
    Vue.prototype.$gtrAuths = gtrAuths
    // Vue.prototype.$gtrAuthNotify = gtrAuthNotify;
    Vue.directive('gtrAuth', {
      inserted: function (el, binding) {
        const display = el.style.display
        el.style.display = 'none'
        gtrAuth(binding.value).then(isAuth => {
          if (isAuth) {
            el.style.display = display
          }
        })
      },
    })
    Vue.mixin({
      methods: {
        $_crm_trackEvent: trackEvent,
        $trackEvent: trackEvent,
        $gtrSendPageView: gtrTrackSW,
      },
    })
  },
}
// 数据通信
export const globalStateActions = {
  onGlobalStateChange: actions.onGlobalStateChange,
  setGlobalState: actions.setGlobalState,
  offGlobalStateChange: actions.offGlobalStateChange,
}
// 子应用路由勾子
export const routerHooks = {
  beforeEach(to, from, next) {
    const appStore = useAppStore()
    // 子应用内部跳转时记录时间
    appStore.$state.loadTime.microStartTime = new Date().getTime()
    // 记录进入子应用时当前时间
    gtrCollectTrackCommonData({
      entry_time: new Date().getTime(),
      name: to?.meta?.title,
    })
  },
  beforeResolve(to, from, next) {
    // todo 埋点页面加载时间
  },
  afterEach(to, from) {
    const gtrBreadcrumbShow = Reflect.has(to.meta, 'gtrBreadcrumbShow')
      ? to.meta.gtrBreadcrumbShow
      : true
    const appStore = useAppStore()
    // store记录子应用加载完毕时间
    appStore.$state.loadTime.endTime = new Date().getTime()
    let subBreadcrumb = []
    if (gtrBreadcrumbShow) {
      subBreadcrumb = to.matched
        .filter(item => {
          return item.meta.title && item.path
        })
        .map(item => {
          return {
            path: item.path,
            title: item.meta.title,
          }
        })
    }
    globalStateActions.setGlobalState({ gtrBreadcrumbShow, subBreadcrumb })
  },
}
// 子系统切换loading定时器
let timer
// qiankun生命周期
// todo 需优化 子应用快速切换时，只会触发beforeLoad周期，后续钩子均不会执行
export const lifeCycles = {
  // 仅执行一次，激活后不再执行
  beforeLoad({ props, ...config }) {
    const appStore = useAppStore()
    timer && clearTimeout(timer)
    appStore.setMicroAppLoadingStauts('loading')
    // 设置当前激活的微应用信息
    appStore.setActiveMicroAppConfig(config)
    microAppSendUseMemoryLog(config.name, 'beforeLoad')
    // 接入性能指标收集包
    setPerformanceTiming(config)
    console.log('log ==> micro app beforeLoad')
  },
  // 应用每次激活都会执行
  beforeMount({ props, ...config }) {
    const appStore = useAppStore()
    timer && clearTimeout(timer)
    appStore.setMicroAppLoadingStauts('loading')
    // 设置当前激活的微应用信息
    appStore.setActiveMicroAppConfig(config)
    // 设置当前子系统的报错过滤
    changeErrorFilters(config.name)
    microAppSendUseMemoryLog(config.name, 'beforeMount')

    console.log('log ==> micro app beforeMount')
  },
  afterMount({ props, ...config }) {
    const appStore = useAppStore()
    /**
     * bugfix 修复切换子系统时css异步加载，导致页面有明显样式错乱闪屏
     * 暂定方案： 子系统切换增加loading，延时关闭loading
     */
    timer = setTimeout(() => {
      appStore.setMicroAppLoadingStauts('loaded')
    }, 200)
    gtrSendEvents.GTR_EVENT_SEND_MICRO_APP_AFTER_MOUNT(config)
  },
  beforeUnmount() {
    // 子系统卸载前，时区重置，防止子系统单独设置时区 影响其他子系统
    console.log('log ==> micro app beforeUnmount')
    setUserTimezone()
  },
  afterUnmount: [
    initBreadcrumb,
    () => {
      const appStore = useAppStore()
      appStore.setActiveMicroAppConfig(null)
      console.log('log ==> micro app afterUnmount')
    },
    config => {
      microAppSendUseMemoryLog(config.name, 'afterUnmount')
      // 卸载当前子系统的报错过滤
      initErrorFilters()
    },
    () => {
      /**
       * fix:子应用修改title标签，离开时重置为Gattaran
       */
      if (document.title !== process.env.VUE_APP_TITLE) document.title = process.env.VUE_APP_TITLE
    },
  ],
}
// 根据路由返回匹配的微应用信息
export const matchMicroAppRoute = cached(path => {
  const matchLocation = matchMicroAppLocation(path)
  return getMicroAppsQianKunConfig().apps.find(item => {
    return item.appLocation.some(matchLocation)
  })
})
// 返回路由跳转path
export const routerPath = cached(path => {
  const microApp = matchMicroAppRoute(path)
  if (microApp) {
    return microApp.prefix + path
  }
  return path
})
// 返回真实全路径
export const fullPath = cached(path => {
  const microApp = matchMicroAppRoute(path)
  if (microApp) {
    return microApp.activeRule + path
  }
  return path
})
// 基座进行重定向，返回分配子应用路由信息
export const redirectMicroRoutePath = (path, appConfig) => {
  path = appConfig.prefix + path
  return path
}
let microAppErrorHandler
export const addMicroAppErrorHandler = errorHandler => {
  microAppErrorHandler = e => {
    microAppSendErrorLog(e)
    errorHandler(e)
  }
  addErrorHandler(microAppErrorHandler)
}
export const removeMicroAppErrorHandler = () => {
  removeErrorHandler(microAppErrorHandler)
}
